import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import {
    FiArrowLeft,
    FiCheck,
    FiEdit2,
    FiAlertTriangle,
    FiUserPlus,
} from 'react-icons/fi';
import { registrationsApi } from '../../api/endpoints/registrations';
import { profilesApi } from '../../api/endpoints/profiles';
import { Stepper, useToast, StatusBadge } from '../../theme/components';
import { PageHeader } from '../../theme/components/PageHeader';
import { PageButton } from '../../theme/components/PageButton';
import { parseApiError } from '../../api/errors';
import { formatDateDisplay } from '../../utils/dates';
import './ReviewAndFinish.css';

const ReviewAndFinish = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const [apiErrors, setApiErrors] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const {
        childData,
        familyData,
        originalChildData,
        originalFamilyData,
        isEditMode,
    } = location.state || {
        childData: {},
        familyData: {},
    };

    const handleBack = () => {
        navigate('/family-details', {
            state: {
                childData,
                familyData,
                originalChildData,
                originalFamilyData,
                isEditMode,
            },
        });
    };

    // ponytail: when editing, build a diff against the original values
    // and PATCH only changed fields. The BE uses exclude_unset=True so
    // fields not in the body keep their stored value — no wipe on
    // untouched fields.
    const buildDiff = (current, original) => {
        const out = {};
        const base = original || {};
        for (const key of Object.keys(current || {})) {
            const a = current[key];
            const b = base[key];
            const same = a === b || (a == null && b == null) || String(a ?? '') === String(b ?? '');
            if (same) continue;
            // Treat empty strings as "no change" — partial-update endpoint
            // would otherwise null the column. Required fields never
            // reach here empty (form validation blocks Next).
            if (a === '' || a === undefined || a === null) continue;
            out[key] = a;
        }
        return out;
    };

    const handleSubmit = async () => {
        setApiErrors([]);
        setSubmitting(true);
        try {
            if (isEditMode && childData?.id) {
                const childPatch = buildDiff(childData, originalChildData);
                const familyPatch = buildDiff(familyData, originalFamilyData);
                const hasChanges = Object.keys(childPatch).length > 0 || Object.keys(familyPatch).length > 0;

                if (hasChanges) {
                    // Include id only as a safety echo; BE ignores it.
                    if (childData.id != null) childPatch.id = childData.id;
                    await profilesApi.update(childData.id, {
                        childData: childPatch,
                        familyData: familyPatch,
                    });
                    toast.success('Child profile updated successfully.');
                } else {
                    toast.success('No changes to save.');
                }
                setTimeout(() => {
                    navigate(`/profiles/${childData.id}`);
                }, 600);
                return;
            }

            // Create flow stays as POST /register.
            const cleanData = (obj) => {
                const out = {};
                for (const key of Object.keys(obj)) {
                    if (obj[key] !== '') out[key] = obj[key];
                }
                return out;
            };
            await registrationsApi.create({
                childData: cleanData({ ...childData }),
                familyData: cleanData({ ...familyData }),
            });
            toast.success('Registration completed successfully.');
            setTimeout(() => navigate('/profiles'), 600);
        } catch (error) {
            const detail = error?.response?.data?.detail;
            let messages;
            if (Array.isArray(detail)) {
                messages = detail.map((err) => {
                    const field = err.loc[err.loc.length - 1];
                    return `${field}: ${err.msg}`;
                });
            } else {
                messages = [parseApiError(error, 'Registration failed.')];
            }
            setApiErrors(messages);
            toast.error('Registration validation failed — see highlighted fields below.');
        } finally {
            setSubmitting(false);
        }
    };

    const renderValue = (value) => (value ? value : <span className="missing-data">Not provided</span>);

    return (
        <div className="rf-container">
            <PageHeader
                title="Add New Child"
                subtitle="Step 3 of 3 — review and submit"
                icon={FiUserPlus}
            />
            <div className="rf-card">

                <div className="rf-stepper-bar">
                    <Stepper steps={['Child', 'Family', 'Review']} currentStep={2} />
                </div>

                {isEditMode && (
                    <div className="rf-edit-badge">
                        <StatusBadge severity="info">
                            <FiEdit2 size={12} /> Editing existing profile
                        </StatusBadge>
                    </div>
                )}

                {apiErrors.length > 0 && (
                    <div className="rf-error-box">
                        <div className="rf-error-header">
                            <FiAlertTriangle size={18} /> Registration Validation Failed
                        </div>
                        <ul>
                            {apiErrors.map((err, index) => (
                                <li key={index}>{err}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="rf-content">
                    <div className="rf-section">
                        <div className="rf-section-header">
                            <h3>Child Information</h3>
                        </div>
                        <div className="rf-grid">
                            <div className="rf-item">
                                <span className="rf-label">Child No</span>
                                <span className="rf-value">{renderValue(childData.childNo)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Surname</span>
                                <span className="rf-value">{renderValue(childData.surname)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">First Name</span>
                                <span className="rf-value">{renderValue(childData.firstName)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Gender</span>
                                <span className="rf-value">{renderValue(childData.gender)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Birth Date</span>
                                <span className="rf-value">{renderValue(formatDateDisplay(childData.birthDate))}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Birth Weight</span>
                                <span className="rf-value">{renderValue(childData.birthWeight ? `${childData.birthWeight} kg` : '')}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Birth Height</span>
                                <span className="rf-value">{renderValue(childData.birthHeight ? `${childData.birthHeight} cm` : '')}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Birth Hospital</span>
                                <span className="rf-value">{renderValue(childData.birthHospital)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Way of Delivered</span>
                                <span className="rf-value">{renderValue(childData.deliveryWay)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Gestational Age at Birth</span>
                                <span className="rf-value">{renderValue(childData.gestationalAge)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">District (Residence)</span>
                                <span className="rf-value">{renderValue(childData.district)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">GN Devision (Residence)</span>
                                <span className="rf-value">{renderValue(childData.gnDevision)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Registered Date</span>
                                <span className="rf-value">{renderValue(formatDateDisplay(childData.registeredDate))}</span>
                            </div>
                            <div className="rf-item rf-full-width">
                                <span className="rf-label">Address</span>
                                <span className="rf-value">{renderValue(childData.address)}</span>
                            </div>
                        </div>
                    </div>

                    <hr className="rf-divider" />

                    <div className="rf-section">
                        <div className="rf-section-header">
                            <h3>Family Information</h3>
                        </div>
                        <div className="rf-grid">
                            <div className="rf-item">
                                <span className="rf-label">Mother's No</span>
                                <span className="rf-value">{renderValue(familyData.mothersNo)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Mother's Name</span>
                                <span className="rf-value">{renderValue(familyData.mothersName)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Mother's DOB</span>
                                <span className="rf-value">{renderValue(formatDateDisplay(familyData.mothersDob))}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Total No. of Children</span>
                                <span className="rf-value">{renderValue(familyData.totalChildren)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Father's Name</span>
                                <span className="rf-value">{renderValue(familyData.fathersName)}</span>
                            </div>
                            <div className="rf-item">
                                <span className="rf-label">Contact Number</span>
                                <span className="rf-value">{renderValue(familyData.contactNumber)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="rf-footer">
                    <PageButton
                        type="button"
                        variant="ghost"
                        startIcon={<FiArrowLeft />}
                        onClick={handleBack}
                        disabled={submitting}
                    >
                        Back to Edit
                    </PageButton>
                    <PageButton
                        type="button"
                        variant="success"
                        startIcon={submitting ? undefined : <FiCheck />}
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
                                Submitting…
                            </span>
                        ) : (
                            'Confirm & Submit'
                        )}
                    </PageButton>
                </div>
            </div>
        </div>
    );
};

export default ReviewAndFinish;
